/**
 * P3-19/20/21: Science tab 按钮模型 hooks。
 *
 * 复用 Bonfire 的 useButtonModel（每 tick fetchModel 快照订阅），对齐旧
 * Button.update 语义（core.js:951-960）。
 */
import { useCallback, useMemo } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import {
  useButtonModel,
  type ModernButtonController,
} from "../bonfire/useButtonModel";

export interface ScienceButtonSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
  resourceIsLimited: string | boolean;
}

export interface UseScienceButtonModelResult {
  snap: ScienceButtonSnapshot | null;
  modelRef: React.MutableRefObject<Record<string, unknown> | null>;
  controller: ModernButtonController | null;
  getTooltip: () => string;
}

type ControllerCtor = new (game: GamePage) => ModernButtonController;

function getController(
  getter: () => ControllerCtor | undefined,
): ModernButtonController | null {
  const game = getGame();
  const Ctor = getter();
  if (!Ctor || !game) {
    return null;
  }
  return new Ctor(game);
}

function toSnapshot(model: Record<string, unknown>): ScienceButtonSnapshot {
  return {
    name: String(model.name ?? ""),
    description: String(model.description ?? ""),
    visible: !!model.visible,
    enabled: !!model.enabled,
    resourceIsLimited: (model.resourceIsLimited as string | boolean) ?? "",
  };
}

function snapshotEq(
  a: ScienceButtonSnapshot | null,
  b: ScienceButtonSnapshot | null,
): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.name === b.name &&
    a.description === b.description &&
    a.visible === b.visible &&
    a.enabled === b.enabled &&
    a.resourceIsLimited === b.resourceIsLimited
  );
}

export function useScienceButton(
  id: string,
  getCtor: () => ControllerCtor | undefined,
  optsExtra: Record<string, unknown> = {},
): UseScienceButtonModelResult {
  const controller = useMemo(() => getController(getCtor), [getCtor]);

  const createOpts = useCallback(
    () => ({ id, ...optsExtra }),
    [id, optsExtra],
  );

  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toSnapshot,
    snapshotEq,
  );

  const getTooltip = useCallback(() => {
    const g = getGame();
    const helper = window.ButtonModernHelper;
    if (!g || !controller || !helper) {
      return "";
    }
    return helper.getTooltipHTML(controller, { options: createOpts() });
  }, [controller, createOpts]);

  return { snap, modelRef, controller, getTooltip };
}

// ---------------------------------------------------------------------------
// Tech
// ---------------------------------------------------------------------------

function getTechControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    com?: {
      nuclearunicorn?: {
        game?: {
          ui?: { TechButtonController?: ControllerCtor };
        };
      };
    };
  };
  return w.com?.nuclearunicorn?.game?.ui?.TechButtonController;
}

export function useTechButtonModel(
  techName: string,
): UseScienceButtonModelResult {
  return useScienceButton(techName, getTechControllerCtor);
}

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

function getPolicyControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    classes?: {
      ui?: {
        PolicyBtnController?: ControllerCtor;
      };
    };
  };
  return w.classes?.ui?.PolicyBtnController;
}

export function usePolicyButtonModel(
  policyName: string,
): UseScienceButtonModelResult {
  return useScienceButton(policyName, getPolicyControllerCtor);
}

// ---------------------------------------------------------------------------
// Metaphysics / Prestige
// ---------------------------------------------------------------------------

function getPrestigeControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    classes?: {
      ui?: {
        PrestigeBtnController?: ControllerCtor;
      };
    };
  };
  return w.classes?.ui?.PrestigeBtnController;
}

export function usePrestigeButtonModel(
  perkName: string,
): UseScienceButtonModelResult {
  return useScienceButton(perkName, getPrestigeControllerCtor);
}

// ---------------------------------------------------------------------------
// ButtonModern（BurnParagon / TurnHGOff）
// ---------------------------------------------------------------------------

function getBurnParagonControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    classes?: {
      ui?: {
        BurnParagonBtnController?: ControllerCtor;
      };
    };
  };
  return w.classes?.ui?.BurnParagonBtnController;
}

export function useBurnParagonButtonModel(
  name: string,
  description: string,
  handler: () => void,
): UseScienceButtonModelResult {
  return useScienceButton(
    "burnParagon",
    getBurnParagonControllerCtor,
    { name, description, handler },
  );
}

function getTurnHGOffControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    classes?: {
      ui?: {
        turnHGOffButtonController?: ControllerCtor;
      };
    };
  };
  return w.classes?.ui?.turnHGOffButtonController;
}

export function useTurnHGOffButtonModel(
  name: string,
  description: string,
  handler: () => void,
): UseScienceButtonModelResult {
  return useScienceButton(
    "turnHGOff",
    getTurnHGOffControllerCtor,
    { name, description, handler },
  );
}
